import { useAudioPlayer } from '@/lib/audio/audio-player-provider';
import { Loader2, Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type TouchEvent,
} from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Audio playback controls component
 *
 * Features:
 * - Play/pause button
 * - Progress slider with click-to-seek
 * - Time display (current / total)
 * - Loading and error states
 *
 * Must be used within AudioPlayerProvider
 */
interface AudioPlaybackControlsProps {
  segmentBoundariesMs?: number[];
}

export const AudioPlaybackControls = ({
  segmentBoundariesMs = [],
}: AudioPlaybackControlsProps) => {
  const {
    isPlaying,
    currentTime,
    duration,
    isLoading,
    error,
    volume,
    isMuted,
    play,
    pause,
    seek,
    setVolume,
  } = useAudioPlayer();
  const volumeSliderRef = useRef<HTMLInputElement | null>(null);
  const activeTouchIdRef = useRef<number | null>(null);
  const [isVolumeTouchDragging, setIsVolumeTouchDragging] = useState(false);

  /**
   * Format milliseconds to MM:SS display
   */
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  /**
   * Handle range slider change for seeking
   */
  const handleSeek = (e: ChangeEvent<HTMLInputElement>) => {
    const newTime = Number(e.target.value);
    seek(newTime);
  };

  /**
   * Handle volume slider change (0-100 mapped to 0-1)
   */
  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextVolume = Number(e.target.value) / 100;
    setVolume(nextVolume);
  };

  const applyVolumePercent = useCallback(
    (nextPercent: number) => {
      const clampedPercent = Math.max(0, Math.min(nextPercent, 100));
      setVolume(clampedPercent / 100);
    },
    [setVolume],
  );

  const updateVolumeFromTouchY = useCallback(
    (clientY: number) => {
      const slider = volumeSliderRef.current;
      if (!slider) return;

      const sliderRect = slider.getBoundingClientRect();
      if (sliderRect.height <= 0) return;

      const percent = ((sliderRect.bottom - clientY) / sliderRect.height) * 100;
      applyVolumePercent(percent);
    },
    [applyVolumePercent],
  );

  const handleVolumeTouchStart = (event: TouchEvent<HTMLInputElement>) => {
    const touch = event.changedTouches[0];
    if (!touch) return;

    activeTouchIdRef.current = touch.identifier;
    setIsVolumeTouchDragging(true);
    updateVolumeFromTouchY(touch.clientY);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isVolumeTouchDragging) return;

    const handleTouchMove = (event: globalThis.TouchEvent) => {
      const activeTouchId = activeTouchIdRef.current;
      if (activeTouchId === null) return;

      const touch = Array.from(event.changedTouches).find(
        (currentTouch) => currentTouch.identifier === activeTouchId,
      );
      if (!touch) return;

      updateVolumeFromTouchY(touch.clientY);
      event.preventDefault();
    };

    const finishTouchDrag = (event: globalThis.TouchEvent) => {
      const activeTouchId = activeTouchIdRef.current;
      if (activeTouchId === null) return;

      const didEndActiveTouch = Array.from(event.changedTouches).some(
        (currentTouch) => currentTouch.identifier === activeTouchId,
      );
      if (!didEndActiveTouch) return;

      activeTouchIdRef.current = null;
      setIsVolumeTouchDragging(false);
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', finishTouchDrag);
    window.addEventListener('touchcancel', finishTouchDrag);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', finishTouchDrag);
      window.removeEventListener('touchcancel', finishTouchDrag);
    };
  }, [isVolumeTouchDragging, updateVolumeFromTouchY]);

  const clampedCurrentTime = Math.max(0, Math.min(currentTime, duration));
  const progressPercent =
    duration > 0 ? Math.min((clampedCurrentTime / duration) * 100, 100) : 0;
  const volumePercent = Math.round((isMuted ? 0 : volume) * 100);
  const segmentTicks = segmentBoundariesMs
    .filter((boundaryMs) => boundaryMs > 0 && boundaryMs < duration)
    .map((boundaryMs) => ({
      boundaryMs,
      leftPercent: (boundaryMs / duration) * 100,
    }));

  const volumeIcon =
    volumePercent === 0 ? (
      <VolumeX className="h-4 w-4" />
    ) : volumePercent < 45 ? (
      <Volume1 className="h-4 w-4" />
    ) : (
      <Volume2 className="h-4 w-4" />
    );

  // Show error state
  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-xl border p-3 text-sm">
        <span>{error}</span>
      </div>
    );
  }

  // Don't show controls if no audio is loaded
  if (duration === 0) {
    return null;
  }

  return (
    <div className="z-20 mb-2 flex w-full flex-col rounded-xl border bg-white p-3">
      <div className="flex items-center gap-2 sm:gap-3">
        <Button
          size="icon-sm"
          onClick={isPlaying ? pause : play}
          className="border-primary-border bg-primary h-9 w-9 shrink-0 rounded-full text-white shadow-xs hover:opacity-90"
          aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <Pause className="h-4 w-4 fill-white" />
          ) : (
            <Play className="h-4 w-4 fill-white" />
          )}
        </Button>

        <div className="min-w-0 flex-1">
          <div className="text-muted-foreground flex items-center justify-between text-[11px] sm:text-xs">
            <span className="flex items-center gap-2 tabular-nums">
              {formatTime(clampedCurrentTime)}
              {isLoading && (
                <span>
                  <Loader2 className="h-3 w-3 animate-spin" />
                </span>
              )}
            </span>
            <span className="tabular-nums">{formatTime(duration)}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min={0}
              max={duration}
              value={clampedCurrentTime}
              onChange={handleSeek}
              style={{
                background: `linear-gradient(90deg, var(--primary) 0%, var(--primary) ${progressPercent}%, var(--muted) ${progressPercent}%, var(--muted) 100%)`,
              }}
              className="focus-visible:ring-ring/70 [&::-webkit-slider-thumb]:border-primary-border [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-track]:bg-muted [&::-moz-range-thumb]:border-primary-border [&::-moz-range-thumb]:bg-primary h-2 w-full cursor-pointer appearance-none rounded-full bg-transparent focus-visible:ring-2 focus-visible:outline-none [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
              aria-label="Playback progress"
            />

            {segmentTicks.length > 0 && (
              <div className="pointer-events-none absolute inset-x-0 top-[9.5px]">
                {segmentTicks.map((tick) => (
                  <span
                    key={tick.boundaryMs}
                    className="bg-primary-border absolute -translate-x-1/2 rounded-full"
                    style={{ left: `${tick.leftPercent}%` }}>
                    <span className="block h-[8px] w-[2px]" />
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground h-8 w-8 rounded-full"
              aria-label="Open volume controls">
              {volumeIcon}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="center"
            sideOffset={8}
            className="border-border/70 bg-background/95 w-auto min-w-0 rounded-xl border p-2 shadow-lg backdrop-blur-sm">
            <div className="flex h-30 w-10 flex-col items-center justify-between gap-1">
              <span className="text-muted-foreground text-[10px] tabular-nums">
                {volumePercent}%
              </span>
              <div className="relative flex h-24 w-full items-center justify-center">
                <input
                  ref={volumeSliderRef}
                  type="range"
                  min={0}
                  max={100}
                  value={volumePercent}
                  onInput={handleVolumeChange}
                  onChange={handleVolumeChange}
                  onTouchStart={handleVolumeTouchStart}
                  style={{
                    background: `linear-gradient(90deg, var(--primary) 0%, var(--primary) ${volumePercent}%, var(--muted) ${volumePercent}%, var(--muted) 100%)`,
                  }}
                  className="focus-visible:ring-ring/70 [&::-webkit-slider-thumb]:border-primary-border [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-track]:bg-muted [&::-moz-range-thumb]:border-primary-border [&::-moz-range-thumb]:bg-primary h-1.5 w-24 -rotate-90 cursor-pointer touch-none appearance-none rounded-full bg-transparent focus-visible:ring-2 focus-visible:outline-none [&::-moz-range-thumb]:size-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:-mt-[5px] [&::-webkit-slider-thumb]:size-3.5 [&::-webkit-slider-thumb]:translate-y-px [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border"
                  aria-label="Volume level"
                />
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
